import {
  PRODUCT_TYPE_CONDITIONS,
  type ProductTypeSlug,
} from "@/lib/generated/product-types";

function getConditionsForCategory(
  productTypeSlug: ProductTypeSlug,
  labelFormatter: (label: string, productName: string) => string,
  productName: string,
) {
  const conditions = PRODUCT_TYPE_CONDITIONS[productTypeSlug];
  if (!conditions) return [];
  return conditions.map((condition) => ({
    value: condition.value,
    label: labelFormatter(condition.label, productName),
  }));
}

export const PRODUCT_CATEGORY_CONFIG = {
  pallet: {
    type: {
      field: "type" as const,
      categories: [
        { value: "stringer", label: "Stringer Pallets" },
        { value: "block", label: "Block Pallets" },
        { value: "wing", label: "Winged Pallets" },
      ],
    },
    entry: {
      field: "entry" as const,
      categories: [
        { value: "2-way", label: "2-Way Entry Pallets" },
        { value: "4-way", label: "4-Way Entry Pallets" },
      ],
    },
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "pallets",
        (label) => label + " Pallets",
        "Pallets",
      ),
    },
    size: {
      field: "size" as const,
      categories: [
        { value: "48 x 40", label: "48x40 Pallets (Standard)" },
        { value: "36 x 36", label: "36x36 Pallets" },
        { value: "42 x 42", label: "42x42 Pallets" },
        { value: "45 x 45", label: "45x45 Pallets" },
        { value: "48 x 48", label: "48x48 Pallets" },
        { value: "800 x 1200mm", label: "800x1200mm Pallets" },
        { value: "1000 x 1200mm", label: "1000x1200mm Pallets" },
        { value: "Mixed Load", label: "Mixed Size Pallets" },
        { value: "Custom", label: "Custom Size Pallets" },
      ],
    },
    material: {
      field: "material" as const,
      categories: [
        { value: "Wood", label: "Wood Pallets" },
        { value: "Plastic", label: "Plastic Pallets" },
        { value: "Metal", label: "Metal Pallets" },
      ],
    },
  },

  gaylord: {
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "gaylord-boxes",
        (label) => label + " Gaylord Boxes",
        "Gaylord Boxes",
      ),
    },
    type: {
      field: "type" as const,
      categories: [
        { value: "8-Wall", label: "8-Wall Boxes" },
        { value: "7-Wall", label: "7-Wall Boxes" },
        { value: "6-Wall", label: "6-Wall Boxes" },
        { value: "5-Wall", label: "5-Wall Boxes" },
        { value: "4-Wall", label: "4-Wall Boxes" },
        { value: "3-Wall", label: "3-Wall Boxes" },
        { value: "2-Wall", label: "2-Wall Boxes" },
        { value: "1-Wall", label: "1-Wall Boxes" },
      ],
    },
    bottoms: {
      field: "bottoms" as const,
      categories: [
        { value: "Full Flap Bottoms", label: "Full Flap Bottoms" },
        { value: "Partial Flap Bottoms", label: "Partial Flap Bottoms" },
        {
          value: "Partial Flap Bottoms with Slip Sheets",
          label: "Partial Flap with Slip Sheets",
        },
        { value: "Trey Bottoms", label: "Trey Bottoms" },
        { value: "No Bottoms", label: "No Bottoms" },
      ],
    },
  },

  ibctote: {
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "ibc-totes",
        (label) => label + " IBC Totes",
        "IBC Totes",
      ),
    },
    size: {
      field: "size" as const,
      categories: [
        { value: "275 Gallon ", label: "275 Gallon Totes" },
        { value: "330 Gallon", label: "330 Gallon Totes" },
        { value: "500 Gallon ", label: "500 Gallon Totes" },
      ],
    },
  },

  plasticdrum: {
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "plastic-drums",
        (label) => label + " Plastic Drums",
        "Plastic Drums",
      ),
    },
    material: {
      field: "material" as const,
      categories: [
        { value: "HDPE", label: "HDPE Plastic Drums" },
        { value: "LDPE", label: "LDPE Plastic Drums" },
        { value: "Other", label: "Other Material Drums" },
      ],
    },
  },

  metaldrum: {
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "metal-drums",
        (label) => label + " Metal Drums",
        "Metal Drums",
      ),
    },
  },

  shippingbox: {
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "shipping-boxes",
        (label) => label + " Shipping Boxes",
        "Shipping Boxes",
      ),
    },
    material: {
      field: "material" as const,
      categories: [
        { value: "Cardboard", label: "Cardboard Boxes" },
        { value: "Corrugated", label: "Corrugated Boxes" },
      ],
    },
  },

  cardboardbale: {
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "cardboard-bales",
        (label) => label + " Cardboard Bales",
        "Cardboard Bales",
      ),
    },
  },

  woodcrate: {
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "wood-crates",
        (label) => label + " Wood Crates",
        "Wood Crates",
      ),
    },
    material: {
      field: "material" as const,
      categories: [
        { value: "Oak", label: "Oak Wood Crates" },
        { value: "Pine", label: "Pine Wood Crates" },
        { value: "Other", label: "Other Wood Crates" },
      ],
    },
  },

  plasticcrate: {
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "plastic-crates",
        (label) => label + " Plastic Crates",
        "Plastic Crates",
      ),
    },
    material: {
      field: "material" as const,
      categories: [
        { value: "HDPE", label: "HDPE Plastic Crates" },
        { value: "LDPE", label: "LDPE Plastic Crates" },
        { value: "Other", label: "Other Material Crates" },
      ],
    },
  },

  bulkbag: {
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "bulk-bags",
        (label) => label + " Bulk Bags",
        "Bulk Bags",
      ),
    },
    type: {
      field: "type" as const,
      categories: [
        { value: "U-Panel", label: "U-Panel Bulk Bags" },
        { value: "4-Panel", label: "4-Panel Bulk Bags" },
        { value: "Circular", label: "Circular Bulk Bags" },
        { value: "Baffled", label: "Baffled Bulk Bags" },
      ],
    },
    top: {
      field: "top" as const,
      categories: [
        { value: "Open Top", label: "Open Top Bulk Bags" },
        { value: "Sprout Top", label: "Sprout Top Bulk Bags" },
        { value: "Duffle Top", label: "Duffle Top Bulk Bags" },
        { value: "Baffled", label: "Baffled Top Bulk Bags" },
      ],
    },
    material: {
      field: "material" as const,
      categories: [
        { value: "Polypropylene", label: "Polypropylene Bulk Bags" },
        { value: "Other", label: "Other Material Bulk Bags" },
      ],
    },
  },

  woodenspool: {
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "wooden-spools",
        (label) => label + " Wooden Spools",
        "Wooden Spools",
      ),
    },
    material: {
      field: "material" as const,
      categories: [
        { value: "Oak", label: "Oak Wooden Spools" },
        { value: "Pine", label: "Pine Wooden Spools" },
        { value: "Other", label: "Other Wood Spools" },
      ],
    },
  },

  lumber: {
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "lumber",
        (label) => label + " Lumber",
        "Lumber",
      ),
    },
    material: {
      field: "material" as const,
      categories: [
        { value: "Oak", label: "Oak Lumber" },
        { value: "Pine", label: "Pine Lumber" },
        { value: "Other", label: "Other Wood Lumber" },
      ],
    },
  },

  plasticpallet: {
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "plastic-pallets",
        (label) => label + " Plastic Pallets",
        "Plastic Pallets",
      ),
    },
    type: {
      field: "type" as const,
      categories: [
        { value: "Nestable", label: "Nestable Plastic Pallets" },
        { value: "Stackable", label: "Stackable Plastic Pallets" },
        { value: "Rackable", label: "Rackable Plastic Pallets" },
        { value: "Drum Pallet", label: "Drum Pallets" },
        { value: "Block", label: "Block Plastic Pallets" },
        { value: "EURO", label: "EURO Plastic Pallets" },
      ],
    },
  },

  equipment: {
    equipmenttype: {
      field: "equipmenttype" as const,
      categories: [
        { value: "Forklift", label: "Forklifts" },
        { value: "Pallet Jack", label: "Pallet Jacks" },
        { value: "Stretch Wrapper", label: "Stretch Wrappers" },
        { value: "Conveyor System", label: "Conveyor Systems" },
        { value: "Strapping Machine", label: "Strapping Machines" },
        { value: "Banding Machine", label: "Banding Machines" },
        { value: "Shrink Wrapper", label: "Shrink Wrappers" },
        { value: "Pallet Rack", label: "Pallet Racks" },
        { value: "Other", label: "Other Equipment" },
      ],
    },
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "equipment",
        (label) => label + " Equipment",
        "Equipment",
      ),
    },
    powersource: {
      field: "powersource" as const,
      categories: [
        { value: "Electric", label: "Electric Powered Equipment" },
        { value: "Gas", label: "Gas Powered Equipment" },
        { value: "Diesel", label: "Diesel Powered Equipment" },
        { value: "Battery", label: "Battery Powered Equipment" },
        { value: "Propane", label: "Propane Powered Equipment" },
        { value: "Manual", label: "Manual Equipment" },
        { value: "N/A", label: "N/A Power Source" },
      ],
    },
  },

  movingbox: {
    boxsize: {
      field: "boxsize" as const,
      categories: [
        { value: "Small", label: "Small Moving Boxes" },
        { value: "Medium", label: "Medium Moving Boxes" },
        { value: "Large", label: "Large Moving Boxes" },
        { value: "Extra Large", label: "Extra Large Moving Boxes" },
        { value: "Wardrobe", label: "Wardrobe Boxes" },
        { value: "Dish Pack", label: "Dish Pack Boxes" },
        { value: "Mirror/Picture", label: "Mirror/Picture Boxes" },
        { value: "File Box", label: "File Boxes" },
        { value: "Mixed Sizes", label: "Mixed Size Boxes" },
      ],
    },
    boxtype: {
      field: "boxtype" as const,
      categories: [
        { value: "Standard", label: "Standard Moving Boxes" },
        { value: "Wardrobe", label: "Wardrobe Moving Boxes" },
        { value: "Dish Pack", label: "Dish Pack Moving Boxes" },
        { value: "Mirror/Picture", label: "Mirror/Picture Moving Boxes" },
        { value: "File Box", label: "File Moving Boxes" },
        { value: "Book Box", label: "Book Boxes" },
      ],
    },
    condition: {
      field: "condition" as const,
      categories: getConditionsForCategory(
        "moving-boxes",
        (label) => label + " Moving Boxes",
        "Moving Boxes",
      ),
    },
  },
} as const;
